import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { INVENTORY_API_URL, MATERIAL_TRANSFERS_API_URL } from '../../constants';
import { SourcePoRequestLinks, type SourcePoRequestFields } from '../sourceLinks/SourcePoRequestLinks';

type EndSnap = {
  regionField: { name?: string; id?: string; county?: string; state?: string; closestCity?: string };
  deliveryAddressId?: string | null;
  deliveryAddress?: string;
  afeNumber?: string;
};

type Line = {
  materialLineKind?: string;
  sku?: string;
  inventoryItemId?: string;
  lineCondition?: string;
  quantity?: number | null;
  footage?: number | null;
};

type TransferApi = {
  id?: string;
  transferNumber?: string;
  status?: string;
  region?: string;
  movementDateUtc?: string;
  transferFrom?: EndSnap;
  transferTo?: EndSnap;
  lines?: Line[];
  receivedAtUtc?: string | null;
  receivedByName?: string | null;
  receiptFileName?: string | null;
};

function formatStatus(s: string | undefined): string {
  const t = (s ?? '').trim();
  if (!t) {
    return '—';
  }
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function MaterialTransferSidePanel(props: {
  transferId: string;
  onClose: () => void;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  onOpenFullForm: (id: string) => void;
}): ReactElement {
  const { transferId, onClose, authFetch, onOpenFullForm } = props;
  const [t, setT] = useState<TransferApi | null>(null);
  const [lineSources, setLineSources] = useState<Record<string, SourcePoRequestFields>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    setT(null);
    try {
      const res = await authFetch(
        `${MATERIAL_TRANSFERS_API_URL}/${encodeURIComponent(transferId)}`
      );
      if (!res.ok) {
        setErr(
          res.status === 404
            ? 'This transfer was not found or is outside your regions.'
            : 'Could not load transfer.'
        );
        return;
      }
      setT((await res.json()) as TransferApi);
    } catch {
      setErr('Could not load transfer.');
    } finally {
      setLoading(false);
    }
  }, [authFetch, transferId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!t?.lines?.length) {
      setLineSources({});
      return;
    }
    const rawIds = t.lines
      .map((l) => l.inventoryItemId)
      .filter((x): x is string => Boolean(x && x.trim()));
    const ids: string[] = [];
    for (const id of rawIds) {
      if (!ids.includes(id)) {
        ids.push(id);
      }
    }
    if (ids.length === 0) {
      setLineSources({});
      return;
    }
    let cancel = false;
    void (async () => {
      const map: Record<string, SourcePoRequestFields> = {};
      await Promise.all(
        ids.map(async (id) => {
          try {
            const res = await authFetch(`${INVENTORY_API_URL}/${encodeURIComponent(id)}`);
            if (!res.ok) {
              return;
            }
            const j = (await res.json()) as SourcePoRequestFields;
            if (!cancel) {
              map[id] = {
                sourcePurchaseOrderId: j.sourcePurchaseOrderId,
                sourcePurchaseOrderNumber: j.sourcePurchaseOrderNumber,
                sourceProcurementRequestId: j.sourceProcurementRequestId,
                sourceRequestNumber: j.sourceRequestNumber
              };
            }
          } catch {
            /* ignore per-line */
          }
        })
      );
      if (!cancel) {
        setLineSources(map);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [t, authFetch]);

  const st = (t?.status ?? '').trim();
  const pillClass = st ? `status-pill mt-status-${st.toLowerCase().replace(/\s+/g, '')}` : 'status-pill';

  return (
    <aside className="side-panel" aria-label="Material Transfer details">
      <div className="side-panel-header">
        <h3>{t?.transferNumber?.trim() || 'Material Transfer'}</h3>
        <div className="side-panel-actions">
          <button type="button" className="icon-close" onClick={onClose} aria-label="Close panel">
            ×
          </button>
        </div>
      </div>
      <div className="side-panel-body">
        {loading && <p className="hint">Loading…</p>}
        {err && <p className="danger-text">{err}</p>}
        {!loading && !err && t && (
          <>
            <p>
              <span className={pillClass}>{formatStatus(t.status)}</span>
            </p>
            <p className="hint">Region: {t.region ?? '—'}</p>
            <p className="hint">
              Move: {t.movementDateUtc ? t.movementDateUtc.replace('T', ' ').slice(0, 19) + ' UTC' : '—'}
            </p>
            {(t.status ?? '').toLowerCase() === 'planned' && (
              <p className="hint">Planned — attach a movement ticket on the full form to complete and move inventory.</p>
            )}
            {t.receivedByName && (
              <p className="hint">
                Received by {t.receivedByName}
                {t.receivedAtUtc ? ` on ${t.receivedAtUtc.replace('T', ' ').slice(0, 16)} UTC` : ''}
                {t.receiptFileName ? ` · ${t.receiptFileName}` : ''}
              </p>
            )}

            <h4>From</h4>
            <p className="panel-value" style={{ whiteSpace: 'pre-wrap' }}>
              {t.transferFrom?.regionField?.name?.trim() || t.transferFrom?.regionField?.id || '—'}
            </p>
            <p className="hint" style={{ whiteSpace: 'pre-wrap' }}>
              {t.transferFrom?.deliveryAddress?.trim() || '—'}
            </p>
            <p className="hint">AFE#: {t.transferFrom?.afeNumber?.trim() || '—'}</p>

            <h4>To</h4>
            <p className="panel-value" style={{ whiteSpace: 'pre-wrap' }}>
              {t.transferTo?.regionField?.name?.trim() || t.transferTo?.regionField?.id || '—'}
            </p>
            <p className="hint" style={{ whiteSpace: 'pre-wrap' }}>
              {t.transferTo?.deliveryAddress?.trim() || '—'}
            </p>
            <p className="hint">AFE#: {t.transferTo?.afeNumber?.trim() || '—'}</p>

            <h4>Lines</h4>
            {!(t.lines && t.lines.length) && <p className="hint">No lines</p>}
            {t.lines && t.lines.length > 0 && (
              <ul className="mt-transfer-line-list">
                {t.lines.map((l, i) => {
                  const src = l.inventoryItemId ? lineSources[l.inventoryItemId] : undefined;
                  return (
                    <li key={(l.inventoryItemId ?? '') + i}>
                      <span className="mono-id">{l.sku}</span> — {l.lineCondition ?? '—'}
                      {l.quantity != null && `, qty ${l.quantity}`}
                      {l.footage != null && `, ft ${l.footage}`}
                      <div className="mono-id hint" style={{ fontSize: '0.8rem' }}>
                        Inv: {l.inventoryItemId}
                      </div>
                      {src && (
                        <SourcePoRequestLinks
                          sourcePurchaseOrderId={src.sourcePurchaseOrderId}
                          sourcePurchaseOrderNumber={src.sourcePurchaseOrderNumber}
                          sourceProcurementRequestId={src.sourceProcurementRequestId}
                          sourceRequestNumber={src.sourceRequestNumber}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="panel-actions" style={{ marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => t.id && onOpenFullForm(t.id)}
                disabled={!t.id}
              >
                Open full form
              </button>
            </div>
            <p className="mono-id hint" style={{ fontSize: '0.8rem', marginTop: '0.75rem' }}>
              Id: {t.id}
            </p>
          </>
        )}
      </div>
    </aside>
  );
}
